import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Fund } from "../../types/Fund";
import { getFunds, deleteFundById, addOrUpdateFund } from "@/api";
import { toast } from "sonner";
import { FormValues } from "./FundFormModal/useFundFormModalController";
import { useForm } from "react-hook-form";

interface FundFormValues {
  fundId: number;
  name: string;
  fundKey?: string;
  fundCalendarId: number;
}

interface UseFundsController {
  funds: Fund[];
  filteredFunds: Fund[];
  isLoading: boolean;
  error: unknown;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  refresh: () => void;
  deleteFund: (fundId: number) => Promise<boolean>;
  updateFund: (fund: Partial<Fund>) => Promise<boolean>;
  createFund: (fund: Partial<Fund>) => Promise<boolean>;
  // Modal state
  isAddFormOpen: boolean;
  isEditFormOpen: boolean;
  isDeleteDialogOpen: boolean;
  selectedFund: Fund | undefined;
  // Core modal actions
  openModal: (type: "add" | "edit" | "delete", fund?: Fund) => void;
  closeModal: (type: "add" | "edit" | "delete", open?: boolean) => void;
  // Core form actions
  handleFundForm: (values: FormValues, isEdit?: boolean) => Promise<void>;
  handleDeleteFund: (fundId: number) => Promise<void>;
  submitFundForm: (values: FundFormValues, isEdit?: boolean) => Promise<void>;
  confirmDelete: () => Promise<void>;
}

export const useFundsController = (): UseFundsController => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState<Fund | undefined>(undefined);

  const {
    data: funds = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["funds"],
    queryFn: async () => getFunds(),
  });

  const refresh = () => refetch();

  const processApiRequest = async (
    apiCall: () => Promise<boolean>,
    successMessage: string,
    errorMessage: string,
    onSuccess?: () => void
  ): Promise<boolean> => {
    try {
      const result = await apiCall();
      if (result) {
        toast.success(successMessage);
        refresh();
        onSuccess?.();
        return true;
      } else {
        toast.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error(`Error: ${errorMessage}`, error);
      toast.error(`An error occurred while ${errorMessage.toLowerCase()}`);
      return false;
    }
  };

  // Unified modal handling
  const openModal = (type: "add" | "edit" | "delete", fund?: Fund) => {
    if (fund) setSelectedFund(fund);

    if (type === "add") setIsAddFormOpen(true);
    else if (type === "edit") setIsEditFormOpen(true);
    else if (type === "delete") setIsDeleteDialogOpen(true);
  };

  const closeModal = (type: "add" | "edit" | "delete", open?: boolean) => {
    if (open === true) return;

    if (type === "add") setIsAddFormOpen(false);
    else if (type === "edit") {
      setIsEditFormOpen(false);
      setSelectedFund(undefined);
    } else if (type === "delete") {
      setIsDeleteDialogOpen(false);
      setSelectedFund(undefined);
    }
  };

  const deleteFund = async (fundId: number): Promise<boolean> => {
    return processApiRequest(
      () => deleteFundById(fundId),
      "Fund deleted successfully",
      "Failed to delete fund",
      () => closeModal("delete")
    );
  };

  const updateFund = async (fund: Partial<Fund>): Promise<boolean> => {
    return processApiRequest(
      () => addOrUpdateFund(fund),
      "Fund updated successfully",
      "Failed to update fund",
      () => closeModal("edit")
    );
  };

  const createFund = async (fund: Partial<Fund>): Promise<boolean> => {
    return processApiRequest(
      () => addOrUpdateFund(fund),
      "Fund created successfully",
      "Failed to create fund",
      () => closeModal("add")
    );
  };

  const submitFundForm = async (values: FundFormValues, isEdit = false) => {
    const fundKey =
      values.fundKey ||
      (isEdit
        ? selectedFund?.fundKey
        : values.name.substring(0, 3).toUpperCase());

    if (isEdit && selectedFund) {
      await updateFund({
        fundId: selectedFund.fundId,
        name: values.name,
        fundKey,
        fundCalendarId: values.fundCalendarId,
      });
    } else {
      await createFund({
        name: values.name,
        fundKey,
        fundCalendarId: values.fundCalendarId,
      });
    }
  };

  const confirmDelete = async () => {
    if (!selectedFund) return;
    await deleteFund(selectedFund.fundId);
  };

  const handleFundForm = async (values: FormValues, isEdit = false) => {
    // Map FormValues to FundFormValues
    const fundFormValues: FundFormValues = {
      fundId: isEdit && selectedFund ? selectedFund.fundId : 0, // 0 or undefined for add
      name: values.name,
      fundKey: values.fundKey,
      fundCalendarId: values.fundCalendarId ?? 0,
    };
    await submitFundForm(fundFormValues, isEdit);
  };

  const handleDeleteFund = async (fundId: number) => {
    await deleteFund(fundId);
  };

  const filteredFunds = useMemo(() => {
    if (!funds) return [];

    const searchTerm = (searchQuery ?? "").trim().toLowerCase();
    if (searchTerm === "") return funds;

    return funds.filter((fund) => fund.name.toLowerCase().includes(searchTerm));
  }, [searchQuery, funds]);

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      fundKey: "",
      fundCalendarId: 0,
    },
  });

  useEffect(() => {
    if (isAddFormOpen || isEditFormOpen) {
      form.reset({
        name: selectedFund?.name || "",
        fundKey: selectedFund?.fundKey || "",
        fundCalendarId: selectedFund?.fundCalendarId || 0,
      });
    }
  }, [isAddFormOpen, isEditFormOpen, selectedFund, form]);

  return {
    funds,
    filteredFunds,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    refresh,
    deleteFund,
    updateFund,
    createFund,
    // Modal state
    isAddFormOpen,
    isEditFormOpen,
    isDeleteDialogOpen,
    selectedFund,
    // Core methods
    openModal,
    closeModal,
    handleFundForm,
    handleDeleteFund,
    submitFundForm,
    confirmDelete,
  };
};
